import { DocumentFormat } from '@common/types/submission-placement-task-config';
import { getProposalMarketingSummaryConfig } from '@common/utils';
import { useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Box, Button, Stack } from '@common-components';
import { BrandingMode, PlacementType } from 'enums';
import { useBoolean } from 'hooks';
import { messages } from 'i18n';
import { PartialSubmission } from 'types';
import { logger } from 'utils';
import SplitButton from 'components/SplitButton';
import { MarketingUpdateRoutes } from 'broker/broker-routes';
import Stepper from 'broker/components/common/Stepper';
import { RouteKey } from 'broker/enums';
import { useNavigate, useNavigateToStep } from 'broker/hooks';
import { useConvertApi } from 'broker/hooks/useConvertApi';
import { InnerPageProps } from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/types';
import { PdfGenerationStatus } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/types';
import { useBrandConfig } from 'broker/pages/SubmissionWorkspacePage/contexts/BrandConfigProvider';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import { marketingUpdateSteps } from './types';

interface FooterProps extends InnerPageProps {
  currentStep: MarketingUpdateRoutes;
  onSaveForLater: (fileId?: string) => Promise<void>;
  isSubmitting: boolean;
  pdfGenerationStatus: PdfGenerationStatus;
  navigateToMarketingUpdateEmail: () => void;
  editorContent: string | undefined;
  omitEmailStep?: boolean;
  pdfFileId?: string;
  setBrandingMode: (brandingMode: BrandingMode | undefined) => void;
  submission: PartialSubmission;
}

export default function Footer({
  currentStep,
  onSaveForLater,
  isSubmitting,
  pdfGenerationStatus,
  setIsDirty,
  navigateToMarketingUpdateEmail,
  editorContent,
  omitEmailStep,
  onClose,
  pdfFileId,
  setBrandingMode,
  submission,
}: FooterProps) {
  const navigateToStep = useNavigateToStep();
  const navigate = useNavigate();
  const { marketingWorkspaceUrl } = useWorkspaceUrls();
  const [cloningPdfToDocx, { on: setCloningPdfToDocx, off: setNotCloningPdfToDocx }] = useBoolean(false);
  const { clonePdfToDocx } = useConvertApi();
  const { isClientBranded, isLoading: loadingBrandConfig } = useBrandConfig();
  const isLoading = cloningPdfToDocx || loadingBrandConfig;

  // Get Marketing Summary config from placement task to determine preferred format and branding
  const marketingSummaryConfig = getProposalMarketingSummaryConfig(submission);

  const handleSaveForLater = async ({ shouldClose, fileId }: { shouldClose: boolean; fileId?: string }) => {
    flushSync(() => {
      setIsDirty(false);
    });
    await onSaveForLater(fileId);

    if (omitEmailStep) {
      onClose();
      return;
    }

    if (shouldClose) {
      navigate(marketingWorkspaceUrl, { routeKey: RouteKey.Marketing, state: {} });
    }
  };

  const onEmailStepProceed = async (newPdfFileId: string) => {
    await handleSaveForLater({ shouldClose: false, fileId: newPdfFileId });

    if (omitEmailStep) {
      onClose();
      return;
    }

    navigateToMarketingUpdateEmail();
  };

  const onStepChanged = (index: number) => {
    navigateToStep(marketingUpdateSteps[index], {
      routeKey: RouteKey.MarketingUpdate,
    });
  };

  const isUploadStepDisabled = useMemo(
    () =>
      currentStep === MarketingUpdateRoutes.Upload &&
      [PdfGenerationStatus.Loading, PdfGenerationStatus.Error].includes(pdfGenerationStatus),
    [currentStep, pdfGenerationStatus],
  );

  const getComposeStepButtons = () => {
    const flowBrandedButton = {
      label: messages.marketingUpdate.buttons.nextGeneratePDF,
      disabled: isUploadStepDisabled,
      onClick: () => {
        setBrandingMode(BrandingMode.Flow);
        onStepChanged(marketingUpdateSteps.indexOf(currentStep) + 1);
      },
    };

    const clientBrandedButton = {
      label: messages.marketingUpdate.buttons.nextGenerateBrandedPDF,
      disabled: isUploadStepDisabled,
      onClick: () => {
        setBrandingMode(BrandingMode.Organization);
        onStepChanged(marketingUpdateSteps.indexOf(currentStep) + 1);
      },
    };

    // Only show client branded button if client branding is available
    if (!isClientBranded) {
      return [flowBrandedButton];
    }

    // Determine button order based on config
    // if no config, fallback to the legacy logic of direct placements preferring client branding
    const preferClientBranded =
      marketingSummaryConfig?.clientBranded ?? submission.placementType === PlacementType.Direct;

    return preferClientBranded ? [clientBrandedButton, flowBrandedButton] : [flowBrandedButton, clientBrandedButton];
  };

  const getUploadStepButtons = () => {
    const proceedWithPdfButton = {
      label: omitEmailStep
        ? messages.marketingUpdate.buttons.savePdf
        : messages.marketingUpdate.buttons.nextComposeEmail,
      disabled: !editorContent,
      onClick: async () => {
        if (pdfFileId) {
          await onEmailStepProceed(pdfFileId);
        }
      },
    };

    const convertToDocxButton = {
      label: omitEmailStep
        ? messages.marketingUpdate.buttons.saveDoc
        : messages.marketingUpdate.buttons.nextComposeEmailWithDoc,
      disabled: !editorContent,
      onClick: async () => {
        if (pdfFileId) {
          try {
            setCloningPdfToDocx();
            const docFileId = await clonePdfToDocx(pdfFileId);
            await onEmailStepProceed(docFileId.id);
          } catch (e: any) {
            logger.log('error', {
              message: 'Failed to convert PDF to DOCX in Marketing Update',
              error: e,
              pdfFileId,
            });
          } finally {
            setNotCloningPdfToDocx();
          }
        }
      },
    };

    // Determine button order based on config
    // if no config, fallback to the legacy logic of direct placements preferring DOCX
    const preferDocx =
      marketingSummaryConfig?.format === DocumentFormat.DOCX || submission.placementType === PlacementType.Direct;

    return preferDocx ? [convertToDocxButton, proceedWithPdfButton] : [proceedWithPdfButton, convertToDocxButton];
  };

  const getProceedButtonOptionsByStep = () => {
    switch (currentStep) {
      case MarketingUpdateRoutes.Compose:
        return getComposeStepButtons();
      case MarketingUpdateRoutes.Upload:
        return getUploadStepButtons();
      default:
        return [];
    }
  };

  return (
    <Stack direction="row" gap={1} justifyContent="center">
      <Box flex={1} />
      <Stack flex={1} alignItems="center">
        <Stepper
          activeStepIndex={marketingUpdateSteps.indexOf(currentStep)}
          stepsLength={2}
          handleChangeStep={onStepChanged}
        />
      </Stack>
      <Stack flex={1} direction="row" gap={1} justifyContent="flex-end" alignItems="center">
        <Button
          variant="outlined"
          onClick={() => handleSaveForLater({ shouldClose: true, fileId: pdfFileId })}
          loading={isSubmitting}
          disabled={isUploadStepDisabled}
        >
          {messages.marketingUpdate.buttons.saveForLater}
        </Button>
        <SplitButton
          loading={isLoading}
          key={currentStep}
          config={{
            options: getProceedButtonOptionsByStep(),
          }}
          variant="contained"
        />
      </Stack>
    </Stack>
  );
}
